"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.hrOvertimeQuerySchema = exports.hrOvertimeRejectSchema = exports.hrOvertimeApproveSchema = exports.hrOvertimeRequestSchema = exports.hrOvertimeStatusEnum = exports.hrAttendanceCorrectionQuerySchema = exports.hrAttendanceCorrectionRejectSchema = exports.hrAttendanceCorrectionApproveSchema = exports.hrAttendanceCorrectionRequestSchema = exports.hrAttendanceCorrectionStatusEnum = exports.hrRecomputeAttendanceSchema = exports.hrMyAttendanceQuerySchema = exports.hrAttendanceQuerySchema = exports.hrManualAttendanceEventSchema = exports.hrAttendanceIngestSchema = exports.hrAttendanceIngestEventSchema = exports.hrAttendanceStatusEnum = exports.hrAttendanceSourceEnum = exports.hrAttendanceEventTypeEnum = void 0;
const zod_1 = require("zod");
/**
 * HR attendance contracts — Phase 1A (docs/HR_PHASE1_ARCHITECTURE.md §7, §8).
 *
 * Three request surfaces:
 *   - INGEST  — machine events from a device / integration. Carries a dedup
 *               dimension (`sourceEventId` or an explicit `idempotencyKey`) so
 *               a re-send is a no-op, never a duplicate attendance effect.
 *   - MANUAL  — a hand-entered punch, always attributed to the operator and
 *               always distinguishable from a biometric event by `source`.
 *   - QUERY   — always date-range bounded and cursor-paginated; the attendance
 *               table is expected to grow without limit.
 */
exports.hrAttendanceEventTypeEnum = zod_1.z.enum(['CLOCK_IN', 'CLOCK_OUT', 'BREAK_START', 'BREAK_END']);
exports.hrAttendanceSourceEnum = zod_1.z.enum(['MANUAL', 'BIOMETRIC', 'DEVICE', 'MOBILE', 'API', 'IMPORT', 'CORRECTION']);
exports.hrAttendanceStatusEnum = zod_1.z.enum([
    'PRESENT', 'ABSENT', 'HALF_DAY', 'LATE', 'EARLY_EXIT', 'MISSING_PUNCH',
    'WEEKLY_OFF', 'HOLIDAY', 'ON_LEAVE', 'OFFICIAL_DUTY', 'ON_CALL', 'FIELD_DUTY', 'WFH',
]);
/** One raw event as a device / integration reports it. */
exports.hrAttendanceIngestEventSchema = zod_1.z.object({
    employeeId: zod_1.z.string().uuid(),
    eventType: exports.hrAttendanceEventTypeEnum,
    eventTime: zod_1.z.coerce.date(),
    /** MANUAL is rejected on the ingest path — see `hrManualAttendanceEventSchema`. */
    source: zod_1.z.enum(['BIOMETRIC', 'DEVICE', 'MOBILE', 'API', 'IMPORT']),
    /** The source system's own id for this event. Primary dedup dimension. */
    sourceEventId: zod_1.z.string().trim().min(1).max(200).optional(),
    deviceId: zod_1.z.string().trim().min(1).max(100).optional(),
    /** Client-generated key, used for dedup when the source has no event id. */
    idempotencyKey: zod_1.z.string().trim().min(1).max(300).optional(),
    rawPayload: zod_1.z.record(zod_1.z.unknown()).optional(),
});
/** A batch of raw events — the normal ingestion shape. */
exports.hrAttendanceIngestSchema = zod_1.z.object({
    events: zod_1.z.array(exports.hrAttendanceIngestEventSchema).min(1).max(1000),
});
/** A hand-entered punch. `source` is fixed to MANUAL server-side; a reason is required. */
exports.hrManualAttendanceEventSchema = zod_1.z.object({
    employeeId: zod_1.z.string().uuid(),
    eventType: exports.hrAttendanceEventTypeEnum,
    eventTime: zod_1.z.coerce.date(),
    reason: zod_1.z.string().trim().min(3).max(2000),
});
/** The attendance-day list. Date range is MANDATORY and bounded. */
exports.hrAttendanceQuerySchema = zod_1.z
    .object({
    employeeId: zod_1.z.string().uuid().optional(),
    departmentId: zod_1.z.string().uuid().optional(),
    branchId: zod_1.z.string().uuid().optional(),
    status: exports.hrAttendanceStatusEnum.optional(),
    from: zod_1.z.coerce.date(),
    to: zod_1.z.coerce.date(),
    cursor: zod_1.z.string().min(1).max(512).optional(),
    limit: zod_1.z.coerce.number().int().positive().max(200).default(50),
})
    .refine((v) => v.to >= v.from, { message: '`to` must not be before `from`', path: ['to'] })
    .refine((v) => (v.to.getTime() - v.from.getTime()) / 86_400_000 <= 186, { message: 'The attendance window may span at most 186 days', path: ['to'] });
/** `GET /attendance/me` — the caller's own days. Date range still bounded. */
exports.hrMyAttendanceQuerySchema = zod_1.z
    .object({
    from: zod_1.z.coerce.date(),
    to: zod_1.z.coerce.date(),
})
    .refine((v) => v.to >= v.from, { message: '`to` must not be before `from`', path: ['to'] })
    .refine((v) => (v.to.getTime() - v.from.getTime()) / 86_400_000 <= 186, { message: 'The attendance window may span at most 186 days', path: ['to'] });
/** Force a day (or an employee's range) to recompute from its events. */
exports.hrRecomputeAttendanceSchema = zod_1.z.object({
    employeeId: zod_1.z.string().uuid(),
    from: zod_1.z.coerce.date(),
    to: zod_1.z.coerce.date(),
});
/**
 * ── ATTENDANCE CORRECTIONS (1A.2, docs/HR_PHASE1_ARCHITECTURE.md §7/§8) ──────
 *
 * A correction ALWAYS resolves to "add one corrective attendance event" — the
 * engine recomputes a day from its events, so the only sound way to correct a
 * day is to feed it the punch that was missing, never to hand-set a status.
 * Request → (matrix configured? PENDING : auto-APPROVED, same posture as
 * `DoctorLeaveService`) → approve writes the CORRECTION event + recomputes;
 * reject leaves the record untouched.
 */
exports.hrAttendanceCorrectionStatusEnum = zod_1.z.enum(['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED']);
exports.hrAttendanceCorrectionRequestSchema = zod_1.z.object({
    attendanceRecordId: zod_1.z.string().uuid(),
    eventType: exports.hrAttendanceEventTypeEnum,
    eventTime: zod_1.z.coerce.date(),
    reason: zod_1.z.string().trim().min(3).max(2000),
});
exports.hrAttendanceCorrectionApproveSchema = zod_1.z.object({
    decisionReason: zod_1.z.string().trim().min(3).max(2000).optional(),
});
exports.hrAttendanceCorrectionRejectSchema = zod_1.z.object({
    decisionReason: zod_1.z.string().trim().min(3).max(2000),
});
exports.hrAttendanceCorrectionQuerySchema = zod_1.z.object({
    employeeId: zod_1.z.string().uuid().optional(),
    status: exports.hrAttendanceCorrectionStatusEnum.optional(),
    cursor: zod_1.z.string().min(1).max(512).optional(),
    limit: zod_1.z.coerce.number().int().positive().max(200).default(50),
});
/**
 * ── OVERTIME (1A.3, docs/HR_PHASE1_ARCHITECTURE.md §7/§8) ───────────────────
 *
 * Self-service request (`employeeId` is always resolved server-side from the
 * caller, exactly like `POST /attendance/events`'s implicit actor) + an HR/
 * manager approval queue. Phase 1 RECORDS and APPROVES only — no payroll
 * consequence is computed here (§13, decision #1).
 */
exports.hrOvertimeStatusEnum = zod_1.z.enum(['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED']);
exports.hrOvertimeRequestSchema = zod_1.z.object({
    workDate: zod_1.z.coerce.date(),
    hours: zod_1.z.coerce.number().positive().max(24),
    preApproved: zod_1.z.boolean().optional(),
    reason: zod_1.z.string().trim().min(3).max(2000),
});
exports.hrOvertimeApproveSchema = zod_1.z.object({
    decisionReason: zod_1.z.string().trim().min(3).max(2000).optional(),
    rateMultiplier: zod_1.z.coerce.number().positive().max(10).optional(),
});
exports.hrOvertimeRejectSchema = zod_1.z.object({
    decisionReason: zod_1.z.string().trim().min(3).max(2000),
});
exports.hrOvertimeQuerySchema = zod_1.z.object({
    employeeId: zod_1.z.string().uuid().optional(),
    status: exports.hrOvertimeStatusEnum.optional(),
    from: zod_1.z.coerce.date().optional(),
    to: zod_1.z.coerce.date().optional(),
    cursor: zod_1.z.string().min(1).max(512).optional(),
    limit: zod_1.z.coerce.number().int().positive().max(200).default(50),
});
//# sourceMappingURL=hr-attendance.schema.js.map